import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGE_ROUTE } from 'src/constants/route';
import BareLayout from 'src/layouts/bare/bare.layout';
import { i18nKey } from 'src/locales/i18n';
import styles from './new-password.module.less';
import { IAuthStore } from 'src/store/auth/auth.store';
import useStore from 'src/hooks/useStore';
import { isTokenValid } from 'src/helpers/jwt.utils';
import { PASSWORD_VALIDATION_RULES } from 'src/constants/validation';

const NewPasswordPage: FC = () => {
	
	const [submitting, setSubmitting] = useState(false);

	const { token } = useParams();
	const [t] = useTranslation();
	const [form] = Form.useForm();
	const navigator = useNavigate();

	const authStore: IAuthStore = useStore('authStore');

	const onFormFinish = async ({
		newPassword,
		newPasswordConfirm
	}: {
		newPassword: string;
		newPasswordConfirm: string;
	}) => {
		if (!token) {
			return;
		}
		if (newPassword != newPasswordConfirm) {
			message.error(t(i18nKey.registration.validation.passwordAndConfirmationPasswordNotMatched));
			return;
		}
		setSubmitting(true);
		const result = await authStore.changeForgottenPassword(
			{
				newPassword,
				newPasswordConfirm
			},
			token
		);

		setSubmitting(false);

		if (result) {
			message.success(t(i18nKey.feedback.changePassSuccess));
			navigator(PAGE_ROUTE.DASHBOARD);
			return;
		}
	};

	useEffect(() => {
		if (!token || !isTokenValid(token)) {
			message.error(t(i18nKey.login.formForgotPassword.invalidLink), 1, ()=>{
				navigator(PAGE_ROUTE.LOGIN);
			});
		}
	}, []);
	return (
		<div className={styles.container}>
			<BareLayout>
				<Row justify="center" gutter={[32, 32]}>
					<Col span={12} className={`common-form-box ${styles.forgotPassword}`} style={{ maxWidth: '618px' }}>
						<Row>
							<Col span={24}>
								<div className={`common-form-title ${styles.title}`}>{t(i18nKey.login.formNewPassword.title)}</div>
								<div className={styles.subTitle}>{t(i18nKey.login.formNewPassword.subTitle)}</div>
							</Col>
						</Row>
						<Form autoComplete="off" layout="vertical" onFinish={onFormFinish} form={form}>
							<Row>
								<Col span={24}>
									<Form.Item
										required={false}
										name="newPassword"
										rules={[
											{ required: true, message: `${t(i18nKey.onboarding.validation.required)}` },
											{
												pattern: new RegExp(PASSWORD_VALIDATION_RULES.REGEX),
												message: `${t(i18nKey.registration.validation.passwordPolicy)}`
											}
										]}
									>
										<Input.Password type="password" placeholder={`${t(i18nKey.login.formNewPassword.createNewPassword)}`} />
									</Form.Item>
									<Form.Item
										required={false}
										name="newPasswordConfirm"
										dependencies={['newPassword']}
										rules={[
											{ required: true, message: `${t(i18nKey.onboarding.validation.required)}` },
											({ getFieldValue }) => ({
												validator(_, value) {
													if (!value || getFieldValue('newPassword') === value) {
														return Promise.resolve();
													}
													return Promise.reject(
														new Error(`${t(i18nKey.registration.validation.passwordAndConfirmationPasswordNotMatched)}`)
													);
												}
											})
										]}
									>
										<Input.Password type="password" placeholder={`${t(i18nKey.login.formNewPassword.reEnterPassword)}`} />
									</Form.Item>
									<Button
										type="primary"
										block
										className={styles.resetPasswordButton}
										loading={submitting}
										htmlType="submit"
									>
										{t(i18nKey.login.formNewPassword.continueButton)}
									</Button>
								</Col>
							</Row>
						</Form>
					</Col>
				</Row>
			</BareLayout>
		</div>
	);
};

export default NewPasswordPage;
